/* @flow */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import type { $Coordinates } from 'CoreComponents/MapProviders/Coordinates';
import type { $Dealer } from 'CoreTypes';
import type { $MapConfiguration } from 'CoreComponents/MapProviders/MapConfiguration';
import type { $StoreState } from '../../types/StoreState';
import type { $WidgetSettings } from '../../types/WidgetSettings';
import ErrorBoundary from 'CoreComponents/ErrorBoundary/ErrorBoundary';
import FieldKeysGeneric from 'CoreConstants/FieldKeysGeneric';
import Keys from '../../constants/WidgetFieldsKeys';
import MapProvider from 'CoreComponents/MapProviders/MapProvider';
import JapanHereMaps from 'CoreComponents/MapProviders/JapanHereMaps';
import SearchResultsMapModal from '../SearchResults/SearchResultsMapModal';
import { MapProviders } from 'CoreConstants/MapProviders';
import { TrackingAttributes } from 'CoreConstants/Tracking';
import { getConfigurationTranslationValue } from 'CoreUtils/WidgetUtils';
import { getEnvConfiguration } from '../../environments';
import { getWidgetFieldValueByKey } from 'CoreUtils/WidgetFieldUtils';
import { setPreviewedDealer } from '../../store/dealer';

type $Props = {
  results: Array<$Dealer>,
  settings: $WidgetSettings,
  configuration: $MapConfiguration,
  selectDealerCallback: Function,
  hereMapsLanguage?: string,
  coordinates?: ?$Coordinates,
  searchTerm: string,
  setPreviewedDealer: Function,
  localeLanguage: boolean,
  mapGesturesTouch: string,
  mapGesturesScroll: string,
  mapGesturesScrollMac: string,
  languageRtl: boolean,
  initialZoomConfig?: number,
  initialPositionConfig?: $Coordinates,
};

class SearchResultsMap extends Component<$Props> {
  handleDealerClick = (outletId: string) => {
    this.props.setPreviewedDealer(outletId, TrackingAttributes.LINK_POSITION_DSW_DEALER_MAP);
  };

  render() {
    const { results, configuration, coordinates, settings: { market } } = this.props;
    const markers: Array<$Coordinates> = [];

    results.forEach((dealer: $Dealer) => {
      if (dealer && dealer.baseInfo && dealer.baseInfo.externalId &&
        dealer.address && dealer.address.latitude && dealer.address.longitude) {
        markers.push({
          lat: dealer.address.latitude,
          lng: dealer.address.longitude,
          outletId: dealer.baseInfo.externalId
        });
      }
    });

    return (
      <div className="dsw-search-results-map">
        <ErrorBoundary>
          {market === 'JP' ?
            <JapanHereMaps
              map={{
                markers,
                onDealerClick: this.handleDealerClick,
                centralPoint: coordinates
              }}
            /> :
            <MapProvider
              market={market || ''}
              searchTerm={this.props.searchTerm}
              provider={MapProviders.HERE_MAPS}
              rtl={this.props.languageRtl}
              initialZoomConfig={this.props.initialZoomConfig}
              initialPositionConfig={this.props.initialPositionConfig}
              map={{
                configuration,
                showControls: true,
                scrollZoom: false,
                markers,
                clustering: true,
                animateTransitions: true,
                onDealerClick: this.handleDealerClick,
                hereMapsLanguage: this.props.hereMapsLanguage,
                renderCentralPoint: true,
                centralPoint: coordinates
              }}
              mapGestureHandlingEnabled={true}
              mapGestureHandlingOptions={{
                translations: {
                  touch: this.props.mapGesturesTouch,
                  scroll: this.props.mapGesturesScroll,
                  scrollMac: this.props.mapGesturesScrollMac
                }
              }}
            />
          }
        </ErrorBoundary>
        <SearchResultsMapModal />
      </div>
    );
  }
}

const mapStateToProps = (state: $StoreState) => ({
  settings: state.dsw.settings,
  configuration: getEnvConfiguration(state.dsw.settings.env).mapProviders,
  hereMapsLanguage: getWidgetFieldValueByKey(Keys.HERE_MAPS_LANGUAGE, state.dsw),
  selectDealerCallback: state.dsw.settings.selectDealerCallback,
  searchTerm: state.dsw.search.searchTerm,
  localeLanguage: !!(state.dsw.configuration && state.dsw.configuration.languages &&
    state.dsw.configuration.languages[0] && state.dsw.configuration.languages[0].localeLanguage),
  languageRtl: !!(state.dsw.configuration && state.dsw.configuration.languages &&
    state.dsw.configuration.languages[0] && state.dsw.configuration.languages[0].rtl),
  mapGesturesTouch: getConfigurationTranslationValue(
    state.dsw.translations || [],
    FieldKeysGeneric.MAP_GESTURES_ZOOM_TOUCH_LABEL
  ),
  mapGesturesScroll: getConfigurationTranslationValue(
    state.dsw.translations || [],
    FieldKeysGeneric.MAP_GESTURES_ZOOM_SCROLL_LABEL
  ),
  mapGesturesScrollMac: getConfigurationTranslationValue(
    state.dsw.translations || [],
    FieldKeysGeneric.MAP_GESTURES_ZOOM_SCROLL_MAC_LABEL
  )
});

const mapDispatchToProps: Object = {
  setPreviewedDealer
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchResultsMap);
